import type { FC } from 'react';
import { useCallback, useEffect } from 'react';
import type { SelectProps } from './Select';
import { APP_LIST } from '../../constants';
import CommonSelect from './CommonSelect';
import { useCheckAuth, UserRole } from 'features/login';
import NoAccessField from '../layout/NoAccessField';
import { useFormContext } from 'react-hook-form';

interface Application {
  name: string;
  code: string | number;
}

interface SelectListProps {
  options: 'all' | 'short';
  multiples?: boolean;
}

type SelectApplicationsProps = Partial<
  Pick<
    SelectProps,
    | 'handleChange'
    | 'name'
    | 'inputLabel'
    | 'placeholder'
    | 'required'
    | 'disabled'
    | 'dataTestId'
  >
> &
  Partial<SelectListProps>;

const SelectApplications: FC<SelectApplicationsProps> =
  ({
     handleChange,
     name,
     inputLabel,
     placeholder,
     multiples,
     required,
     disabled,
     dataTestId,
   }) => {
    const { watch, setValue } = useFormContext();
    const { isSuperuser, apps } = useCheckAuth();
    const listMapper = useCallback(({ name, code }: Application) => ({
      label: name,
      value: code,
    }), []);

    const isRoleSuperuser = watch('role') === UserRole.SUPERUSER;

    const options =
      APP_LIST
        .filter(app => isRoleSuperuser || isSuperuser ? app : apps?.includes(app.code))
        .map(listMapper);

    useEffect(() => {
      if (isRoleSuperuser) {
        setValue(name ?? 'app', APP_LIST.map(app => app.code),
          { shouldValidate: true, shouldDirty: true });
      }
    }, [isRoleSuperuser, setValue, name, listMapper]);

    return options.length ? (
      <CommonSelect
        name={name ?? 'app'}
        inputLabel={inputLabel ?? 'Application'}
        options={options}
        handleChange={handleChange}
        placeholder={placeholder ?? 'Value'}
        clearable
        multiples={multiples}
        required={required}
        disabled={isRoleSuperuser || disabled}
        dataTestId={dataTestId}
      />
    ) : <NoAccessField text={'Access to applications is not granted. Contact your manager'} />;
  };

export default SelectApplications;
